// Robust, cross-browser date parsing for the progress media pages (photos + videos).
//
// `new Date(string)` is unreliable in Safari/Firefox: it rejects non-strict-ISO
// formats (e.g. "2026-05-19 00:00:00") and the date-array shapes that Chrome's lenient
// parser happens to accept. The backend serializes LocalDate as an array
// ([year, month(1-based), day]) because the app's ObjectMapper leaves
// WRITE_DATES_AS_TIMESTAMPS at its default (true) — that array becomes the string
// "2026,5,19" when used as an object key. Handle arrays, comma-separated values, and
// ISO strings explicitly, then fall back to dayjs.
//
// Extracted from ProgressPhotosPage (which already used these correctly) so
// ProgressVideosPage can share the exact same logic — the videos page previously used
// raw `new Date(video.videoDate)` and `grouped[video.videoDate]`, which both broke on
// the array shape (Safari/Firefox NaN headers, and — engine-independently — a
// "2026,5,19" grouping key that the delete-by-date / save-comment endpoints, expecting
// a YYYY-MM-DD LocalDate path/param, cannot parse).

import dayjs from 'dayjs';

/**
 * Parse a backend date value (LocalDate array, comma-joined string, or ISO string)
 * into a local Date, or null when unparseable.
 */
export const parseMediaDate = (value: unknown): Date | null => {
    if (value === null || value === undefined || value === '') return null;

    // [year, month(1-12), day, ...] arrays, or comma-joined strings like "2026,5,19"
    const parts: unknown[] | null = Array.isArray(value)
        ? value
        : (typeof value === 'string' && value.includes(',') ? value.split(',') : null);
    if (parts && parts.length >= 3) {
        const [year, month, day] = parts.map(Number);
        if (![year, month, day].some(Number.isNaN)) {
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }

    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
        const [, year, month, day] = match;
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = dayjs(str);
    return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Canonical YYYY-MM-DD key. Used for grouping and for any value sent back to the API
 * (delete-by-date, save-comment), which expect a YYYY-MM-DD LocalDate string — NOT the
 * raw "2026,5,19" array-stringification.
 */
export const toISODateKey = (value: unknown): string => {
    const date = parseMediaDate(value);
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** DD/MM/YYYY display string, or '' when unparseable. */
export const formatMediaDate = (value: unknown): string => {
    const date = parseMediaDate(value);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const HEB_DAY_NAMES: string[] = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/** Hebrew weekday name, or '' when unparseable. */
export const getHebrewDayName = (value: unknown): string => {
    const date = parseMediaDate(value);
    if (!date) return '';
    return HEB_DAY_NAMES[date.getDay()];
};
